import { ProductCard } from './index';

export class ProductCardBuilder {
  // Check your origin session, if you are Domestic or International
  static isIntl = window.Shopify?.country !== 'US';

  constructor(title, productType) {
    this.title = title;
    this.productType = productType;
  }

  /**
   * Set up properties like the node where Product Card instance will be render and product id value
   * @param {String} template - template identifier for the product recommendation
   * @param {Number} idx - index number for each product recommendation card
   * @param {HTMLElement} parentNode - parent node where Product Card instances will be contained
   * @returns {ProductCardBuilder} - ProductCardBuilder object
   */
  makeNode(parentNode) {
    const productCardNode = document.createElement('div');
    productCardNode.classList.add('product-card-c-basis');
    // Set extra base class if provided
    this.baseClassName && productCardNode.classList.add(`${this.baseClassName}__product-card`);
    this.productCardNode = productCardNode;
    parentNode.appendChild(productCardNode);
    return this;
  }

  makeExtraBaseClass(className) {
    this.baseClassName = className;
    return this;
  }

  makeCarouselItemNode(template, idx) {
    this.productCardNode.setAttribute('id', `carousel_${template}_card_${idx}`);
    return this;
  }

  /**
   * Adjust a previous url with new values and set it as a property
   * @param {String} url - previous product url value
   * @returns {ProductCardBuilder} - ProductCardBuilder object
   */
  makeUrl(url) {
    this.productUrl = url || '';
    return this;
  }

  /**
   * Set product id on the builder
   * @param {String|Number} productId - product identifier
   * @returns {ProductCardBuilder} - ProductCardBuilder object
   */
  makeProductId(productId) {
    this.productId = productId;
    this.productCardNode.setAttribute('data-product-id', productId);
    return this;
  }

  /**
   * Set the variant ID from Constructor data
   * @returns {ProductCardBuilder} - ProductCardBuilder object
   */
  makeVariantId() {
    this.variantId = this.availableVariants[0]?.data?.variation_id;
    return this;
  }

  /**
   * Transform placeholder/clearance products by cleaning title and swapping URL
   * Placeholder products have titles like "PLACEHOLDER - Zodiac Gift Cards" and their
   * variant SKU contains the real product handle in pipe-delimited format: "CANCER25|zodiac-gift-cards-digital?variant=123"
   * @returns {ProductCardBuilder} - ProductCardBuilder object
   */
  makePlaceholderTransform() {
    const placeholderPattern = /^((CLEARANCE)|(PLACEHOLDER)) - /;
    if (!placeholderPattern.test(this.title)) return this;

    // Strip the prefix from the title
    this.title = this.title.replace(placeholderPattern, '');

    // Extract the real product URL from the variant SKU field
    const sku = this.availableVariants?.[0]?.data?.sku || '';
    const pipeIndex = sku.indexOf('|');
    if (pipeIndex > -1) {
      const realHandle = sku.substring(pipeIndex + 1);
      if (realHandle) {
        // Set the full URL including the real variant param from the SKU
        // e.g. "/products/zodiac-gift-cards-digital?variant=39672154325043"
        this.productUrl = `/products/${realHandle}`;
        // Mark as placeholder so #buildProductUrl can skip appending its own variant param
        this.isPlaceholder = true;
      }
    }

    return this;
  }

  /**
   * Set available variants and get rid of out of stock variations
   * @param {Array<Object>} variants - Constructor item variations
   * @returns {ProductCardBuilder} - ProductCardBuilder object
   */
  makeVariants(variants = []) {
    this.variants = variants;
    const availableVariants = variants.filter((variant) => this.#getAvailableItem(variant));
    if (availableVariants.length) {
      this.availableVariants = availableVariants;
      return this;
    }

    // Fallback case when there were no available variations
    // Make sure that its image exists in image arrary
    let firstVariant;

    // Confirm first variant style color is contained in images
    if (Array.isArray(this.media)) {
      firstVariant = variants.find((variant) => this.media.some((el) => el.includes(variant.data.barcode)));
    } else {
      firstVariant = variants.find((variant) => this.media.includes(variant.data.barcode));
    }

    // if it was not found, it will choose the first variant no matter what the availability
    if (!firstVariant) {
      firstVariant = variants[0];
    }
    // Bring up all variants that match color parameter
    this.availableVariants = variants.filter((variant) => variant.data.color === firstVariant.data.color);
    return this;
  }

  /**
   * Choose color from first available variant
   * @returns {ProductCardBuilder} - ProductCardBuilder object
   */
  selectColor() {
    const correctVariant = this.availableVariants[0];
    if (!correctVariant) return this;

    if (correctVariant) {
      this.selectedColor = correctVariant.data.color;
      this.imageAlt = this.title;
    }

    if (typeof this.media !== 'string') {
      // media come from sorted in a descending way and they are based on image naming convention
      this.imageUrl = this.media.filter((image) => image.includes(correctVariant.data.barcode))[0];
      this.secondImageUrl = this.media.filter((image) => image.includes(correctVariant.data.barcode))[1];
    }

    // if an image hadn't gotten from media array, it will move to use image_url contained in the variant
    // it also means that we wouldn't need to set up a second media image becuase it doesn't exist
    if (!this.imageUrl) {
      this.imageUrl = correctVariant.data.image_url;
    }

    return this;
  }

  /**
   * Build an Array of color objects based on available styles and colors variants
   * @returns {ProductCardBuilder} - ProductCardBuilder object
   */
  makeOptions() {
    if (!this.availableVariants) return this;

    this.options = [];

    this.availableVariants.forEach((variant) => {
      const isColorExisting = this.options.find(
        (option) => option.color.toLowerCase() === variant.data.color.toLowerCase(),
      );
      !isColorExisting &&
        this.options.push({
          barcode: variant.data.barcode,
          color: variant.data.color,
          variantId: variant.data.variation_id,
        });
    });
    return this;
  }

  /**
   * Check availability based on inventory for Domestic and non-Domestic experiences
   * @param {Object} variant - Constructor API variant object
   * @returns {Boolean} - availability status
   */
  #getAvailableItem(variant) {
    return ProductCardBuilder.isIntl
      ? variant.data.inventory_quantity_international > 0
      : variant.data.inventoryQuantity > 0;
  }

  /**
   * Set up price's properties such as price, compare at price and max compare at price on ProductCardBuilder
   * @returns {ProductCardBuilder} - ProductCardBuilder object
   */
  makePrices() {
    const firstVariantSellable = this.availableVariants.find((variant) => this.#getAvailableItem(variant));

    // Fallback to first variant if no sellable variants exist
    const variantForPrice = firstVariantSellable || this.availableVariants[0];

    // Check if country is not US, then use country-specific price fields
    const country = window.Shopify?.country || 'US';
    const isIntl = country !== 'US';
    const priceField = isIntl ? `price${country}` : 'priceUS';
    const compareAtPriceField = isIntl ? `compareAtPrice${country}` : 'compareAtPriceUS';

    // Use country-specific prices with fallback to 0 if not available
    // It will be helpful to understand when these values are not coming from response
    const maximumCompareAtPrice = Math.max(
      ...this.availableVariants.map((variant) => variant.data[compareAtPriceField] ?? 0),
    );
    this.price = variantForPrice?.data[priceField] ?? 0;
    this.compareAtPrice = variantForPrice?.data[compareAtPriceField] ?? 0;
    this.maxCompareAtPrice = maximumCompareAtPrice;
    return this;
  }

  /**
   * Set up badge property to render it on ProductCard
   * @param {String} badge - JSON stringified badge object
   * @returns {ProductCardBuilder} - ProductCardBuilder object
   */
  makeBadge(badge) {
    // Exit safely if no badge is provided
    if (!badge) return this;

    const rawBadge = JSON.parse(badge);

    // Exit safely if badge data is empty
    if (!Object.keys(rawBadge).length) return this;

    this.badge = rawBadge;
    return this;
  }

  /**
   * Turn on/off badge node
   * @returns {ProductCardBuilder} - ProductCardBuilder object
   */
  makeBadgeVisible() {
    this.isBadgeVisible = true;
    return this;
  }

  /**
   * Set up a reviews property on ProductCardBuilder instance in order to turn on reviews section on ProductCard instance
   * @param {Number} reviews_count - number of reviews by product
   * @param {Number} reviews_average - average rating of reviews
   * @returns {ProductCardBuilder} - ProductCardBuilder object
   */
  makeReviews(reviews_count, reviews_average) {
    // Set the number of stars and reviews count
    this.reviews = { average: reviews_average, count: reviews_count };
    return this;
  }

  /**
   * Prioritize the first viewport's product media without making every card eager.
   * @param {String} fetchPriority - browser fetch priority, usually high for card one
   * @returns {ProductCardBuilder} - ProductCardBuilder object
   */
  makeAboveFoldImagePriority(fetchPriority = 'auto') {
    this.imageLoading = 'eager';
    this.imageFetchPriority = fetchPriority;
    return this;
  }

  makeCardImageHidden(value = false) {
    this.isCardImageHidden = value;
    return this;
  }

  /**
   * Turn on/off media carousel or gallery on product cards
   * @returns {ProductCardBuilder} - ProductCardBuilder Object
   */
  makeCardImageGallery() {
    this.isCardImageGallery = true;
    return this;
  }

  makeVideo(videoSource) {
    this.videoSource = videoSource;
    return this;
  }

  /**
   * Set promo tracking data for GTM select_promotion event
   * @param {String} creative - promotion creative name
   * @param {String} trackingId - unique tracking identifier
   * @returns {ProductTileBuilder} - ProductTileBuilder object
   */
  makePromoTracking(creative, trackingId) {
    this.promoCreative = creative;
    this.trackingId = trackingId;
    return this;
  }

  /**
   * Set media array and secondImageUrl property if it exists
   * @param {Array<string>|String} mediaSource - media array or just a single image url or video url
   * @returns {ProductCardBuilder} - ProductCardBuilder Object
   */
  makeMedia(mediaSource) {
    // Set media as an empty array if no media were provided
    if (!mediaSource) {
      this.media = [];
      return this;
    }

    if (typeof mediaSource === 'string') {
      // This means that there is just one single image
      this.media = [mediaSource];
      return this;
    }

    // Set images array and secondImageUrl property
    this.media = mediaSource;
    if (Array.isArray(mediaSource)) {
      this.secondImageUrl = mediaSource[1] || null;
    }
    return this;
  }

  /**
   * Set promo message on product card based on cached metaobject and as many as product can show
   * @param {Object} param0 - Argument of this funtion
   * @param {HTMLDivElement} param0.messages - HTML element where they are cached
   * @param {Array<String>} param0.tags - specific array product tags
   * @param {Number} param0.date - current date/time in seconds
   * @returns {ProductCardBuilder} - ProductCardBuidler Object
   */
  makePromoMessages({ messages, tags, date }) {
    // Guard condition whether messages or product tag weren't set up
    if (!messages || !tags) return this;
    // Initialize promo message content
    this.promoMessage = '';

    for (const tag of tags) {
      if (tag.match(/"/)) {
        continue;
      }

      const promoMessageElement = messages.querySelector(`div[data-tag="${tag}"]`);
      if (!promoMessageElement) {
        continue;
      }

      let promoMessageStartDate = promoMessageElement?.getAttribute('data-start-date') || '';
      let promoMessageEndDate = promoMessageElement?.getAttribute('data-end-date') || '';

      if (!promoMessageStartDate && !promoMessageEndDate) {
        this.promoMessage = promoMessageElement.querySelector('div')?.innerHTML;
        continue;
      }

      promoMessageStartDate = parseInt(promoMessageStartDate) || 0;
      promoMessageEndDate = parseInt(promoMessageEndDate) || 0;

      // if current date/time is within metaobject entry's 'start date/time' value and 'end date/time' value or
      // if current date/time is on or after metaobject entry's 'start date/time' value
      if (
        (date >= promoMessageStartDate && date <= promoMessageEndDate) ||
        (date >= promoMessageStartDate && !promoMessageEndDate)
      ) {
        this.promoMessage += `${promoMessageElement.querySelector('div')?.innerHTML}\n`;
        continue;
      }
    }

    return this;
  }

  /**
   * Set media switching behavior for desktop devices
   * @returns {ProductCardBuilder} - ProductCardBuilder Object
   */
  makeMediaSwitching() {
    this.mediaSwitch = true;
    return this;
  }

  /**
   * Set color swatches to be rendered on ProductCard
   * @returns {ProductCardBuilder} - ProductCardBuilder Object
   */
  makeSwatches() {
    this.colorSwatches = true;
    return this;
  }

  makeQuickAdd() {
    this.quickAdd = true;
    return this;
  }

  makeConstructorRecommendation() {
    this.productCardNode.setAttribute('data-cnstrc-item', 'recommendation');
    return this;
  }

  /**
   * Set Constructor tracking attributes for autocomplete results
   * @param {Object} product - Constructor API autocomplete product object
   * @param {String} section - Section name (e.g., 'Products')
   * @returns {ProductCardBuilder} - ProductCardBuilder object
   */
  setConstructorAutocompleteAttributes(product, section = 'Products') {
    if (!this.productCardNode || !product) return this;

    this.productCardNode.setAttribute('data-cnstrc-item', 'autosuggest');
    this.productCardNode.setAttribute('data-cnstrc-item-section', section);
    this.productCardNode.setAttribute('data-cnstrc-item-id', product.data?.id || '');
    this.productCardNode.setAttribute('data-cnstrc-item-name', product.value || '');
    this.productCardNode.setAttribute('data-cnstrc-item-variation-id', this.variantId || '');

    const itemPrice = this.#calculateDisplayPrice(product);
    if (itemPrice) {
      this.productCardNode.setAttribute('data-cnstrc-item-price', itemPrice);
    }

    return this;
  }

  /**
   * Set Constructor tracking attributes on the product card node
   * Populates data-cnstrc-* attributes with values from Constructor API response
   * @param {Object} product - Constructor API product object
   * @returns {ProductCardBuilder} - ProductCardBuilder object
   */
  setConstructorAttributes(product) {
    if (!this.productCardNode || !product) return this;

    const itemId = product.data?.id || '';
    const itemName = product.value || '';
    const variationId = product.data?.variation_id || '';
    const strategyId = product.strategy?.id || '';
    const itemPrice = this.#calculateDisplayPrice(product);

    this.productCardNode.setAttribute('data-cnstrc-item-id', itemId);
    this.productCardNode.setAttribute('data-cnstrc-item-name', itemName);
    this.productCardNode.setAttribute('data-cnstrc-item-variation-id', variationId);
    this.productCardNode.setAttribute('data-cnstrc-strategy-id', strategyId);
    if (itemPrice) {
      this.productCardNode.setAttribute('data-cnstrc-item-price', itemPrice);
    }
    return this;
  }

  /**
   * Set GTM tracking data attributes on the product card node
   * Separated from Constructor attributes to avoid leaking into recommendation containers
   * @param {Object} product - Constructor API product object
   * @returns {ProductCardBuilder} - ProductCardBuilder object
   */
  setTrackingAttributes(product) {
    if (!this.productCardNode || !product) return this;

    const price = this.#calculateDisplayPrice(product);
    const compareAtPrice = this.#calculateDisplayPrice(product, 'compareAt') || '';
    const isUS = window.Shopify?.country === 'US';
    const reviewCount = product.data?.reviews_count || 0;
    const reviewAverage = product.data?.reviews_average || 0;
    const tags = product.data?.tags || '';
    const barcode = this.availableVariants?.[0]?.data?.barcode || '';

    this.productCardNode.setAttribute('data-product-type', product.data?.product_type || '');
    this.productCardNode.setAttribute('data-compare-at-price', compareAtPrice);
    this.productCardNode.setAttribute('data-style', barcode);
    this.productCardNode.setAttribute('data-new-arrival', String(tags.includes('New Arrival')));
    this.productCardNode.setAttribute(
      'data-is-marked-down',
      String(Number(price) < Number(compareAtPrice) && Number(compareAtPrice) > 0),
    );
    this.productCardNode.setAttribute('data-has-reviews', String(reviewCount > 0));
    this.productCardNode.setAttribute('data-review-count', String(reviewCount));
    this.productCardNode.setAttribute('data-review-average', String(reviewAverage));
    this.productCardNode.setAttribute('data-shows-quadpay', String(isUS && Number(price) > 14));
    const firstAvailable = this.availableVariants?.[0];
    const inventory = firstAvailable
      ? Number(
          ProductCardBuilder.isIntl
            ? firstAvailable.data?.inventory_quantity_international
            : firstAvailable.data?.inventoryQuantity,
        )
      : 0;
    this.productCardNode.setAttribute('data-almost-gone', String(inventory > 1 && inventory < 10));

    return this;
  }

  /**
   * Set the constructor pod id
   * @param {String} podId - constructor pod id
   * @returns {ProductTileBuilder} - ProductTileBuilder object
   */
  setPodId(podId) {
    this.podId = podId;
    return this;
  }

  /**
   * Set a location where product card belongs to
   * @param {String} location - place where product card is rendered
   * @returns {ProductTileBuilder} - ProductTileBuilder object
   */
  makeLocation(location) {
    this.location = location;
    return this;
  }

  /**
   * Calculate display price as numeric value (min of sale/retail price)
   * Uses country-specific pricing when available
   * @param {Object} product - Constructor API product object
   * @returns {string} - Numeric price value as string
   * @private
   */
  #calculateDisplayPrice(product, type = 'display') {
    const data = product.data || {};

    // Get country-specific price fields
    const country = window.Shopify?.country || 'US';
    const priceField = country !== 'US' ? `price${country}` : 'priceUS';
    const compareAtPriceField = country !== 'US' ? `compareAtPrice${country}` : 'compareAtPriceUS';

    const regularPrice = parseFloat(data[priceField] ?? data.priceUS) || 0;
    const compareAtPrice = parseFloat(data[compareAtPriceField] ?? data.compareAtPriceUS) || 0;

    // Return only the compare-at price when requested
    if (type === 'compareAt') {
      return compareAtPrice > 0 ? compareAtPrice.toString() : '';
    }

    // Return the lower of the two prices (sale price logic)
    if (regularPrice > 0 && compareAtPrice > 0) {
      return Math.min(regularPrice, compareAtPrice).toString();
    }
    return (regularPrice || compareAtPrice || '').toString();
  }

  /**
   * Build a new ProductCard instance according to ProductCardBuilder's properties
   * @returns {ProductCard} - return a ProductCard instance
   */
  build() {
    return new ProductCard(this);
  }
}
